import { cn } from '@crowdex/ui/lib/utils';
import { Check, Circle, Clock3, LoaderCircle, ShieldAlert, Square, XCircle } from 'lucide-react';
import type { AssistantMemberView } from './assistant-conversation-api';
import type { TaskIconState } from './assistant-model';
import { PersonaMark } from './persona-mark';

/**
 * A persona of the project, by its mark (`PersonaMark`: its avatar, else its glyph and letter on
 * its colour), its colour stable by its place among the project's personas.
 */
export function PersonaAvatar({
  persona,
  personas,
  size = 'md',
  className,
}: {
  persona: Pick<AssistantMemberView, 'agentId' | 'name' | 'avatar'>;
  personas: readonly (Pick<AssistantMemberView, 'agentId'> & { name?: string })[];
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}) {
  const index = Math.max(
    0,
    personas.findIndex((entry) => entry.agentId === persona.agentId),
  );
  return (
    <PersonaMark
      className={className}
      index={index}
      persona={persona}
      shown={personas.flatMap((entry) => (entry.name === undefined ? [] : [entry.name]))}
      size={size}
    />
  );
}

/** A workflow's glyph (D64): its clock, in a neutral ring (amber says "waits on you" only). */
export function WorkflowGlyph({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-muted-foreground',
        className,
      )}
    >
      <Clock3 className="size-3" />
    </span>
  );
}

/** A task's state (D65 Tasks): done, waits on you, running, not begun, failed, stopped. */
export function TaskStateIcon({ state, className }: { state: TaskIconState; className?: string }) {
  const classes = cn('size-4 shrink-0', className);
  if (state === 'succeeded')
    return <Check aria-label="Done" className={cn(classes, 'text-success')} />;
  if (state === 'waiting')
    return <ShieldAlert aria-label="Waits on you" className={cn(classes, 'text-attention')} />;
  if (state === 'running')
    return (
      <LoaderCircle aria-label="Running" className={cn(classes, 'animate-spin text-running')} />
    );
  if (state === 'failed')
    return <XCircle aria-label="Failed" className={cn(classes, 'text-destructive')} />;
  if (state === 'stopped')
    return <Square aria-label="Stopped" className={cn(classes, 'text-subtle-foreground')} />;
  return <Circle aria-label="Not begun" className={cn(classes, 'text-subtle-foreground')} />;
}
